import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ArrowLeft, Save, Check } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/tools/follow-ups")({
  head: () => ({ meta: [{ title: "Follow-Ups" }, { name: "robots", content: "noindex" }] }),
  component: FollowUpsPage,
});

type F = {
  id?: string;
  client_name: string;
  client_email: string | null;
  client_phone: string | null;
  channel: string;
  status: string;
  due_on: string;
  notes: string | null;
  completed_at: string | null;
};

const today = () => new Date().toISOString().slice(0, 10);
const empty = (): F => ({
  client_name: "",
  client_email: "",
  client_phone: "",
  channel: "call",
  status: "pending",
  due_on: today(),
  notes: "",
  completed_at: null,
});

function daysUntil(d: string) {
  const ms = new Date(d + "T00:00:00").getTime() - Date.now();
  return Math.ceil(ms / 86400000);
}

function FollowUpsPage() {
  const [list, setList] = useState<F[]>([]);
  const [current, setCurrent] = useState<F | null>(null);
  const [filter, setFilter] = useState<"open" | "done" | "all">("open");

  const load = async () => {
    const { data } = await supabase.from("follow_ups").select("*").order("due_on", { ascending: true });
    setList((data as any) || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!current || !current.client_name) return toast.error("Client required");
    const payload = { ...current };
    if (current.id) {
      const { error } = await supabase.from("follow_ups").update(payload).eq("id", current.id);
      if (error) return toast.error(error.message);
    } else {
      const { error } = await supabase.from("follow_ups").insert(payload as any);
      if (error) return toast.error(error.message);
    }
    toast.success("Saved"); setCurrent(null); load();
  };
  const del = async (id: string) => { if (confirm("Delete?")) { await supabase.from("follow_ups").delete().eq("id", id); load(); } };
  const complete = async (f: F) => {
    await supabase.from("follow_ups").update({ status: "done", completed_at: new Date().toISOString() }).eq("id", f.id!);
    toast.success("Marked done"); load();
  };

  const filtered = list.filter(f =>
    filter === "all" ? true : filter === "done" ? f.status === "done" : f.status !== "done"
  );

  if (!current) return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Follow-Ups</h1>
          <p className="text-sm text-muted-foreground mt-1">Lead & customer nurture tasks.</p>
        </div>
        <div className="flex gap-2">
          <select className="rounded border border-input bg-background px-2 text-sm" value={filter} onChange={e => setFilter(e.target.value as any)}>
            <option value="open">Open</option>
            <option value="done">Done</option>
            <option value="all">All</option>
          </select>
          <Button onClick={() => setCurrent(empty())}><Plus className="h-4 w-4 mr-1" />New</Button>
        </div>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map(f => {
          const d = daysUntil(f.due_on);
          const overdue = f.status !== "done" && d < 0;
          const soon = f.status !== "done" && d >= 0 && d <= 2;
          return (
            <div key={f.id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold">{f.client_name}</h3>
                  <p className="text-xs text-muted-foreground">{f.channel} · {f.status}</p>
                  <p className={`text-xs mt-1 ${overdue ? 'text-destructive font-semibold' : soon ? 'text-amber-600' : 'text-muted-foreground'}`}>
                    Due {f.due_on} {f.status !== "done" && (overdue ? `(${-d}d overdue)` : `(in ${d}d)`)}
                  </p>
                </div>
                <button onClick={() => del(f.id!)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
              {f.notes && <p className="text-xs mt-2 text-muted-foreground line-clamp-2">{f.notes}</p>}
              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => setCurrent(f)}>Open</Button>
                {f.status !== "done" && <Button size="sm" variant="secondary" onClick={() => complete(f)}><Check className="h-4 w-4 mr-1" />Done</Button>}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <p className="text-muted-foreground text-sm">No follow-ups.</p>}
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => setCurrent(null)}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
        <Button onClick={save}><Save className="h-4 w-4 mr-1" />Save</Button>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div><label className="text-xs text-muted-foreground">Client Name</label><Input value={current.client_name} onChange={e => setCurrent({ ...current, client_name: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Email</label><Input value={current.client_email || ""} onChange={e => setCurrent({ ...current, client_email: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Phone</label><Input value={current.client_phone || ""} onChange={e => setCurrent({ ...current, client_phone: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Due Date</label><Input type="date" value={current.due_on} onChange={e => setCurrent({ ...current, due_on: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Channel</label>
          <select className="w-full rounded border border-input bg-background p-2" value={current.channel} onChange={e => setCurrent({ ...current, channel: e.target.value })}>
            <option value="call">Call</option><option value="email">Email</option><option value="text">Text</option><option value="visit">Visit</option>
          </select>
        </div>
        <div><label className="text-xs text-muted-foreground">Status</label>
          <select className="w-full rounded border border-input bg-background p-2" value={current.status} onChange={e => setCurrent({ ...current, status: e.target.value })}>
            <option value="pending">Pending</option><option value="in_progress">In Progress</option><option value="done">Done</option><option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>
      <Textarea placeholder="Notes" className="mt-4" rows={5} value={current.notes || ""} onChange={e => setCurrent({ ...current, notes: e.target.value })} />
    </div>
  );
}
